import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { sessionRequestAPI, analyticsAPI, getAuthToken } from '../utils/api';
import { useAuth } from './AuthContext';

const SessionRequestContext = createContext();

export const SessionRequestProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [sessionRequests, setSessionRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const sessionRequestsRef = useRef(sessionRequests);
  sessionRequestsRef.current = sessionRequests;

  // Clear state when auth is lost (e.g. 401) so we never call APIs without a token
  useEffect(() => {
    const onAuthLost = () => {
      setSessionRequests([]);
      setLoading(false);
      setEmployeePerformance([]);
      setAnalyticsLoading(false);
      setAnalyticsCache({
        totalLearners: 0,
        completionRate: 0,
        averageRating: 0,
        activeSessions: 0,
        totalSessionsCompleted: 0,
        topPerformers: []
      });
    };
    window.addEventListener('auth-session-expired', onAuthLost);
    return () => window.removeEventListener('auth-session-expired', onAuthLost);
  }, []);

  // Load session requests from API only when authenticated and token is present
  useEffect(() => {
    if (!isAuthenticated || !getAuthToken()) {
      setLoading(false);
      setSessionRequests([]);
      return;
    }
    const loadSessionRequests = async () => {
      if (!getAuthToken()) {
        setLoading(false);
        setSessionRequests([]);
        return;
      }
      try {
        setLoading(true);
        const requests = await sessionRequestAPI.getAll();
        // Transform API data to match expected format
        const transformed = requests.map(req => ({
          id: req.id,
          employeeName: req.employee_name || '',
          sessionName: req.session_name || '',
          attemptsUsed: req.attempts_used || 0,
          maxAttempts: req.max_attempts != null ? req.max_attempts : null,
          lockedDate: req.locked_date ? new Date(req.locked_date).toLocaleDateString() : null,
          requestDate: req.created_at ? new Date(req.created_at).toLocaleString() : null,
          createdAt: req.created_at || null,
          status: req.status || 'pending',
          reason: req.reason || '',
          approvedAt: req.approved_at ? new Date(req.approved_at).toISOString() : null,
          approvalExpiresAt: req.approval_expires_at ? new Date(req.approval_expires_at).toISOString() : null,
          employeeEmail: req.employee_email || '',
          employee: req.employee,
          session: req.session
        }));
        setSessionRequests(transformed);
      } catch (error) {
        console.error('Error loading session requests from API:', error);
        setSessionRequests([]);
      } finally {
        setLoading(false);
      }
    };
    loadSessionRequests();
  }, [isAuthenticated]);
  
  // Employee performance tracking - loaded from API
  const [employeePerformance, setEmployeePerformance] = useState([]);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  
  // Analytics data state
  const [analyticsCache, setAnalyticsCache] = useState({
    totalLearners: 0,
    completionRate: 0,
    averageRating: 0,
    activeSessions: 0,
    totalSessionsCompleted: 0,
    topPerformers: []
  });
  const analyticsCacheRef = useRef(analyticsCache);
  analyticsCacheRef.current = analyticsCache;

  // Load/refresh analytics from API (exposed so dashboard can refresh after deletes)
  const refreshAnalytics = useCallback(async () => {
    if (!getAuthToken()) return;
    try {
      setAnalyticsLoading(true);
      const analyticsData = await analyticsAPI.getAnalytics();
      if (analyticsData) {
        if (analyticsData.employeePerformance) {
          setEmployeePerformance(analyticsData.employeePerformance);
        }
        setAnalyticsCache({
          totalLearners: analyticsData.totalLearners || 0,
          completionRate: analyticsData.completionRate || 0,
          averageRating: analyticsData.averageRating || 0,
          activeSessions: analyticsData.activeSessions || 0,
          totalSessionsCompleted: analyticsData.totalSessionsCompleted || 0,
          topPerformers: analyticsData.topPerformers || []
        });
      }
    } catch (error) {
      console.error('Error loading analytics from API:', error);
      setEmployeePerformance([]);
      setAnalyticsCache({
        totalLearners: 0,
        completionRate: 0,
        averageRating: 0,
        activeSessions: 0,
        totalSessionsCompleted: 0,
        topPerformers: []
      });
    } finally {
      setAnalyticsLoading(false);
    }
  }, []);

  const refreshRequests = useCallback(async () => {
    if (!getAuthToken()) return;
    try {
      const requests = await sessionRequestAPI.getAll();
      const transformed = requests.map(req => ({
        id: req.id,
        employeeName: req.employee_name || '',
        sessionName: req.session_name || '',
        attemptsUsed: req.attempts_used || 0,
        maxAttempts: req.max_attempts != null ? req.max_attempts : null,
        lockedDate: req.locked_date ? new Date(req.locked_date).toLocaleDateString() : null,
        requestDate: req.created_at ? new Date(req.created_at).toLocaleString() : null,
        createdAt: req.created_at || null,
        status: req.status || 'pending',
        reason: req.reason || '',
        approvedAt: req.approved_at ? new Date(req.approved_at).toISOString() : null,
        approvalExpiresAt: req.approval_expires_at ? new Date(req.approval_expires_at).toISOString() : null,
        employeeEmail: req.employee_email || '',
        employee: req.employee,
        session: req.session
      }));
      setSessionRequests(transformed);
    } catch (error) {
      console.error('Error refreshing session requests:', error);
    }
  }, []);

  // Only run analytics when authenticated and token is present; clear cache when not
  useEffect(() => {
    if (!isAuthenticated || !getAuthToken()) {
      setAnalyticsLoading(false);
      setEmployeePerformance([]);
      setAnalyticsCache({
        totalLearners: 0,
        completionRate: 0,
        averageRating: 0,
        activeSessions: 0,
        totalSessionsCompleted: 0,
        topPerformers: []
      });
      return;
    }
    refreshAnalytics();
    const interval = setInterval(refreshAnalytics, 60000);
    return () => clearInterval(interval);
  }, [isAuthenticated, refreshAnalytics]);

  // Keep session requests in sync (important for 24h approval window expiry)
  useEffect(() => {
    if (!isAuthenticated || !getAuthToken()) return;
    refreshRequests();
    const interval = setInterval(refreshRequests, 15000);
    return () => clearInterval(interval);
  }, [isAuthenticated, refreshRequests]);

  const addSessionRequest = useCallback(async (request) => {
    try {
      const newRequest = await sessionRequestAPI.create({
        employee: request.employee,
        session: request.session,
        reason: request.reason || '',
        status: request.status || 'pending',
      });
      
      // Transform and add to local state
      const transformed = {
        id: newRequest.id,
        employeeName: newRequest.employee_name || '',
        sessionName: newRequest.session_name || '',
        attemptsUsed: newRequest.attempts_used || 0,
        maxAttempts: newRequest.max_attempts != null ? newRequest.max_attempts : null,
        lockedDate: newRequest.locked_date ? new Date(newRequest.locked_date).toLocaleDateString() : null,
        requestDate: newRequest.created_at ? new Date(newRequest.created_at).toLocaleString() : null,
        createdAt: newRequest.created_at || null,
        status: newRequest.status || 'pending',
        reason: newRequest.reason || '',
        approvedAt: newRequest.approved_at ? new Date(newRequest.approved_at).toISOString() : null,
        approvalExpiresAt: newRequest.approval_expires_at ? new Date(newRequest.approval_expires_at).toISOString() : null,
        employeeEmail: newRequest.employee_email || '',
        employee: newRequest.employee,
        session: newRequest.session
      };
      setSessionRequests(prev => [transformed, ...prev]);
    } catch (error) {
      console.error('Error adding session request:', error);
      throw error;
    }
  }, []);

  const updateRequestStatus = useCallback(async (requestId, newStatus, extra = {}) => {
    try {
      const request = sessionRequestsRef.current.find((req) => req.id === requestId);
      if (!request) return;
      
      const updated = await sessionRequestAPI.update(requestId, {
        status: newStatus,
        ...extra
      });
      
      // Update local state
      setSessionRequests(prev =>
        prev.map(req =>
          req.id === requestId ? {
            ...req,
            status: updated.status || newStatus,
            ...extra,
            attemptsUsed: updated.attempts_used != null ? updated.attempts_used : req.attemptsUsed,
            maxAttempts: updated.max_attempts != null ? updated.max_attempts : req.maxAttempts,
            lockedDate: updated.locked_date ? new Date(updated.locked_date).toLocaleDateString() : req.lockedDate,
            approvedAt: updated.approved_at ? new Date(updated.approved_at).toISOString() : null,
            approvalExpiresAt: updated.approval_expires_at ? new Date(updated.approval_expires_at).toISOString() : null
          } : req
        )
      );
    } catch (error) {
      console.error('Error updating session request:', error);
      throw error;
    }
  }, []);

  // Update employee performance when they complete sessions
  const updateEmployeePerformance = useCallback((employeeId, sessionCompleted = false, rating = null) => {
    setEmployeePerformance(prev =>
      prev.map(emp => {
        if (emp.id === employeeId) {
          const newSessionsCompleted = sessionCompleted ? emp.sessionsCompleted + 1 : emp.sessionsCompleted;
          const newCompletionRate = Math.round((newSessionsCompleted / emp.totalSessions) * 100);
          const newAverageRating = (rating != null && rating >= 1) ?
            ((emp.averageRating * emp.sessionsCompleted) + rating) / (emp.sessionsCompleted + 1) : 
            emp.averageRating;
          
          return {
            ...emp,
            sessionsCompleted: newSessionsCompleted,
            completionRate: newCompletionRate,
            averageRating: Math.round(newAverageRating * 10) / 10,
            lastActivity: new Date().toISOString().split('T')[0]
          };
        }
        return emp;
      })
    );
  }, []);


  // Stable getter so context consumers do not churn when only the cache snapshot changes
  const getAnalyticsData = useCallback(() => analyticsCacheRef.current, []);

  const contextValue = useMemo(
    () => ({
      sessionRequests,
      loading,
      employeePerformance,
      addSessionRequest,
      updateRequestStatus,
      updateEmployeePerformance,
      getAnalyticsData,
      refreshAnalytics,
      refreshRequests,
    }),
    [
      sessionRequests,
      loading,
      employeePerformance,
      addSessionRequest,
      updateRequestStatus,
      updateEmployeePerformance,
      getAnalyticsData,
      refreshAnalytics,
      refreshRequests,
    ]
  );

  return (
    <SessionRequestContext.Provider value={contextValue}>
      {children}
    </SessionRequestContext.Provider>
  );
};

export const useSessionRequests = () => {
  return useContext(SessionRequestContext);
};
